/**
 * Metrics Dashboard Component
 * 
 * Displays comprehensive metrics and analytics for the
 * AI Command Center, including agent performance and task distribution.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Activity, BarChart3, CheckCircle2, AlertTriangle, 
  Sparkles, TrendingUp, Clock 
} from "lucide-react";
import { AgentAvatars, PriorityColors } from "./constants";

export function MetricsDashboard({ metrics, agents }) {
  if (!metrics) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Carregando metricas...</p>
        </CardContent>
      </Card>
    );
  }

  const { tasks_by_agent, tasks_by_priority, summary, avg_resolution_hours, critical_pending, llm_analyses_count } = metrics;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold">{summary?.total_tasks || 0}</p>
                <p className="text-sm text-muted-foreground">Total de Tarefas</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400 opacity-70" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Ultimos 7 dias</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-400">{summary?.resolved_tasks || 0}</p>
                <p className="text-sm text-muted-foreground">Resolvidas</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400 opacity-70" />
            </div>
            <p className="text-xs text-green-400 mt-2">{summary?.auto_resolution_rate || 0}% automatico</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-400">{summary?.pending_tasks || 0}</p>
                <p className="text-sm text-muted-foreground">Pendentes</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400 opacity-70" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Aguardando acao</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-purple-400">{llm_analyses_count || 0}</p>
                <p className="text-sm text-muted-foreground">Analises LLM</p>
              </div>
              <Sparkles className="w-8 h-8 text-purple-400 opacity-70" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">GPT-4.1</p>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              Desempenho por Agente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(tasks_by_agent || {}).map(([agentId, stats]) => {
              const agent = agents[agentId] || {};
              const avatar = AgentAvatars[agentId] || "🤖";
              const total = stats.total || 0;
              const completed = (stats.completed || 0) + (stats.executed || 0);
              const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
              
              return (
                <div key={agentId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{avatar}</span>
                      <span className="font-medium">{agent.name || agentId}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold">{completed}</span>
                      <span className="text-muted-foreground">/{total}</span>
                    </div>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                      style={{ width: `${rate}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{rate}% resolvido</span>
                    <span>{stats.pending || 0} pendentes</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Tempo Medio de Resolucao
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(avg_resolution_hours || {}).map(([agentId, hours]) => {
              const agent = agents[agentId] || {};
              const avatar = AgentAvatars[agentId] || "🤖";
              
              return (
                <div key={agentId} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{avatar}</span>
                    <div>
                      <p className="font-medium">{agent.name || agentId}</p>
                      <p className="text-xs text-muted-foreground">{agent.role || ""}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {hours !== null ? (
                      <>
                        <p className="text-2xl font-bold">{hours}</p>
                        <p className="text-xs text-muted-foreground">horas</p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">—</p>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Priority Distribution & Critical Tasks */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Distribuicao por Prioridade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { key: "critical", label: "Criticas", color: "bg-red-500" },
                { key: "high", label: "Altas", color: "bg-amber-500" },
                { key: "medium", label: "Medias", color: "bg-blue-500" },
                { key: "low", label: "Baixas", color: "bg-slate-500" },
              ].map(({ key, label, color }) => {
                const count = tasks_by_priority?.[key] || 0;
                const total = Object.values(tasks_by_priority || {}).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((count / total) * 100);
                
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{label}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Tarefas Criticas Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {critical_pending?.length > 0 ? (
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {critical_pending.map((task, i) => {
                  const avatar = AgentAvatars[task.agent] || "🤖";
                  return (
                    <div key={i} className="flex items-center gap-3 p-2 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <span className="text-lg">{avatar}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(task.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge className={PriorityColors[task.priority]}>{task.priority}</Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50 text-green-400" />
                <p>Nenhuma tarefa critica pendente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
